import React, { useState, useEffect, useCallback } from 'react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, Users, UserPlus, Mail, Loader2 } from 'lucide-react';
import { OrganizationTab } from './OrganizationTab';
import { TeamsTab } from './TeamsTab';
import { MembersTab } from './MembersTab';
import { InvitationsTab } from './InvitationsTab';
import { OrganizationDetail, getOrganization } from '@/services/orgTeamApi';

export function OrganizationPage() {
  const { toast } = useToast();
  const { user, activeOrganization } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState<OrganizationDetail | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  // Determine user's role in current org
  const userOrgRole = organization?.members?.find(m => m.user_id === user?.id)?.role || 'member';
  const isOwner = userOrgRole === 'owner';
  const isAdmin = userOrgRole === 'admin' || isOwner;
  const isSuperadmin = user?.is_superadmin === true;

  // Load organization data
  const loadOrganization = useCallback(async (orgId: string) => {
    setIsLoading(true);
    try {
      const data = await getOrganization(orgId);
      setOrganization(data);
    } catch (error) {
      console.error('Failed to load organization:', error);
      toast({
        title: 'Error',
        description: 'Failed to load organization details',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Initialize with active organization
  useEffect(() => {
    if (activeOrganization?.id) {
      setSelectedOrgId(activeOrganization.id);
      loadOrganization(activeOrganization.id);
    } else {
      setIsLoading(false);
    }
  }, [activeOrganization?.id, loadOrganization]);

  // Handle org change
  const handleOrgChange = (orgId: string) => {
    setSelectedOrgId(orgId);
    loadOrganization(orgId);
  };

  // Refresh org data (called by child components after changes)
  const refreshOrganization = useCallback(() => {
    if (selectedOrgId) {
      loadOrganization(selectedOrgId);
    }
  }, [selectedOrgId, loadOrganization]);

  if (!user) {
    return null;
  }

  const hasMultipleOrgs = (user.organizations?.length || 0) > 1;

  return (
    <div className="min-h-screen bg-background">
      <TopNavigation />
      <div className="container mx-auto py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold">Organization</h1>

          {/* Org selector for multi-org users */}
          {hasMultipleOrgs && (
            <Select value={selectedOrgId || ''} onValueChange={handleOrgChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {user.organizations?.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Loading state */}
        {isLoading && (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        )}

        {/* No organization state */}
        {!isLoading && !organization && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
              <h2 className="text-lg font-semibold mb-2">No Organization Selected</h2>
              <p className="text-muted-foreground">
                {user.organizations?.length === 0
                  ? 'You are not a member of any organization.'
                  : 'Select an organization to manage.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Organization management tabs */}
        {!isLoading && organization && (
          <Tabs defaultValue="organization" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="organization" className="gap-2">
                <Building2 className="h-4 w-4" />
                Organization
              </TabsTrigger>
              <TabsTrigger value="teams" className="gap-2">
                <Users className="h-4 w-4" />
                Teams
              </TabsTrigger>
              <TabsTrigger value="members" className="gap-2">
                <UserPlus className="h-4 w-4" />
                Members
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="invitations" className="gap-2">
                  <Mail className="h-4 w-4" />
                  Invitations
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="organization">
              <OrganizationTab
                organization={organization}
                userRole={userOrgRole}
                isSuperadmin={isSuperadmin}
                onUpdate={refreshOrganization}
              />
            </TabsContent>

            <TabsContent value="teams">
              <TeamsTab
                organization={organization}
                userRole={userOrgRole}
                onUpdate={refreshOrganization}
              />
            </TabsContent>

            <TabsContent value="members">
              <MembersTab
                organization={organization}
                currentUserId={user.id}
                userRole={userOrgRole}
                onUpdate={refreshOrganization}
              />
            </TabsContent>

            {isAdmin && (
              <TabsContent value="invitations">
                <InvitationsTab organization={organization} />
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>
    </div>
  );
}
